import { FC, useMemo, useState } from 'react';
import { Grid, Typography, Box, TextField, MenuItem, Chip, CircularProgress, Alert } from '@mui/material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianAssessmentsHook } from '../../../../hooks/useGuardianAcademicHook';
import { useAcademicsFiltersContext } from '../../../../../academics/contexts/academicsFilters.context';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { GuardianAssessment } from '../../../../../../../apis/guardian/sub-apis/useGuardianAcademicService';

const GuardianAssessmentsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const { academicYear, grade } = useAcademicsFiltersContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const { data, isLoading, error } = useGuardianAssessmentsHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
      ...(subjectFilter !== 'all' && { subject: subjectFilter }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(academicYear && { academicYear }),
      ...(grade && { grade }),
    }
  );

  const assessments = useMemo(() => data?.data || [], [data]);
  const totalCount = data?.count ?? 0;

  const subjects = useMemo(() => {
    const uniqueSubjects = Array.from(new Set(assessments.map((a) => a.subject)));
    return uniqueSubjects.sort();
  }, [assessments]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'subject',
      headerName: translateText('guardian.academics.assessments.columns.subject'),
      width: 150,
      flex: 1,
    },
    {
      field: 'title',
      headerName: translateText('guardian.academics.assessments.columns.title'),
      width: 200,
      flex: 1,
    },
    {
      field: 'score',
      headerName: translateText('guardian.academics.assessments.columns.score'),
      width: 100,
      type: 'number',
    },
    {
      field: 'maxScore',
      headerName: translateText('guardian.academics.assessments.columns.maxScore'),
      width: 100,
      type: 'number',
    },
    {
      field: 'percentage',
      headerName: translateText('guardian.academics.assessments.columns.percentage'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const assessment = params.row as GuardianAssessment;
        const percentage = (assessment.score / assessment.maxScore) * 100;
        return `${percentage.toFixed(1)}%`;
      },
    },
    {
      field: 'grade',
      headerName: translateText('guardian.academics.assessments.columns.grade'),
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const grade = params.value as string;
        const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
          'A+': 'success',
          'A': 'success',
          'B+': 'info',
          'B': 'info',
          'C+': 'warning',
          'C': 'warning',
          'D': 'error',
          'F': 'error',
        };
        return (
          <Chip
            label={grade}
            size="small"
            color={colorMap[grade] || 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'date',
      headerName: translateText('guardian.academics.assessments.columns.date'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
  ], [translateText]);

  const rows = useMemo(() => {
    return assessments.map((assessment) => ({
      id: assessment.id,
      subject: assessment.subject,
      title: assessment.title,
      score: assessment.score,
      maxScore: assessment.maxScore,
      grade: assessment.grade,
      date: assessment.date,
    }));
  }, [assessments]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-assessments-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-assessments-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-assessments-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-assessments-container">
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="guardian-assessments-filters"
        >
          <TextField
            select
            label={translateText('guardian.academics.assessments.filters.subject')}
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-assessments-subject-filter"
          >
            <MenuItem value="all">{translateText('common.all')}</MenuItem>
            {subjects.map((subject) => (
              <MenuItem key={subject} value={subject}>
                {subject}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={translateText('guardian.academics.assessments.filters.fromDate')}
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-assessments-from-date-filter"
          />
          <TextField
            label={translateText('guardian.academics.assessments.filters.toDate')}
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-assessments-to-date-filter"
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <PaginatedGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          dataTestId="guardian-assessments-grid"
          serverSide={true}
          totalCount={totalCount}
          currentPage={page}
          currentPageSize={pageSize}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Grid>
    </Grid>
  );
};

export default GuardianAssessmentsView;
